import { computed, ref } from 'vue';
import { createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import type { Account } from '@patsnap-utils/biz';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { controlDeferred, type IControl } from '@eureka/utils/control';
import { packageInfoDeferred } from '@eureka/utils/package-info';
import type { IPackageType } from '@eureka/services/src/apis/common.api.types';

type CreditsControl = IControl & {
    credit_package?: boolean;
};

export const useUserInfo = createSharedComposable(() => {
    const isLogin = ref(false);
    const account = ref<Account | null>(null);
    const control = ref<CreditsControl | null>(null);
    const packageInfo = ref<IPackageType[]>([]);

    const isEnterpriseTenant = computed(() => {
        return !!account.value?._info?.company_id;
    });

    const isPersonalTenant = computed(() => {
        return !isEnterpriseTenant.value;
    });

    const isFreeUser = computed(() => {
        return isPersonalTenant.value && control.value?.freemium === true;
    });

    const isPro = computed(() => {
        return isPersonalTenant.value && !isFreeUser.value;
    });

    const hasCreditPackage = computed(() => {
        return control.value?.credit_package === true;
    });

    const hasEnterpriseFreePackage = computed(() => {
        return isEnterpriseTenant.value && packageInfo.value.some(item => item.package_type === 'FREE');
    });

    const hasEnterpriseTrialPackage = computed(() => {
        return isEnterpriseTenant.value && packageInfo.value.some(item => typeof item.trial_package_left_days !== 'undefined');
    });

    const hasEnterpriseCommercialPackage = computed(() => {
        return isEnterpriseTenant.value && packageInfo.value.some(item => item.package_type === 'COMMERCIAL');
    });

    async function syncUserInfo() {
        try {
            const loggedIn = await isUserLogin();
            isLogin.value = loggedIn;

            if (!loggedIn) {
                account.value = null;
                control.value = null;
                packageInfo.value = [];
                return;
            }

            const [nextAccount, nextControl, nextPackageInfo] = await Promise.all([
                accountDeferred.promise,
                controlDeferred.promise,
                packageInfoDeferred.promise
            ]);

            account.value = nextAccount;
            control.value = nextControl as CreditsControl;
            packageInfo.value = nextPackageInfo;
        } catch (error) {
            account.value = null;
            control.value = null;
            packageInfo.value = [];
            console.warn('[useUserInfo] Failed to sync user info:', error);
        }
    }

    tryOnBeforeMount(syncUserInfo);

    return {
        isLogin,
        account,
        control,
        isEnterpriseTenant,
        isPersonalTenant,
        isFreeUser,
        isPro,
        hasCreditPackage,
        packageInfo,
        hasEnterpriseFreePackage,
        hasEnterpriseTrialPackage,
        hasEnterpriseCommercialPackage,
        syncUserInfo,
    };
});
